import React, { FC, useCallback, useContext } from 'react';
import { useSidebarCollapsed } from './useSidebarCollapsed';
import { usePresentationMode } from './usePresentationMode';

type AppContextType = {
  sidebarCollapsed: boolean;
  spreadSidebar: () => void;
  toggleSidebar: () => void;
  collapseSidebar: () => void;
  presentationMode: boolean;
  setPresentationMode: (newValue: boolean) => void;
};

const AppContext = React.createContext<AppContextType>({
  sidebarCollapsed: false,
  spreadSidebar: () => 1,
  toggleSidebar: () => 1,
  collapseSidebar: () => 1,
  presentationMode: false,
  setPresentationMode: () => 1,
});

export const useAppContext = () => useContext<AppContextType>(AppContext);

const AppContextProvider: FC = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed();
  const [presentationMode, setPresentationMode] = usePresentationMode();

  const spreadSidebar = useCallback(() => setSidebarCollapsed(false), []);
  const collapseSidebar = useCallback(() => setSidebarCollapsed(true), []);
  const toggleSidebar = useCallback(
    () => setSidebarCollapsed(!sidebarCollapsed),
    [sidebarCollapsed],
  );

  return (
    <AppContext.Provider
      value={{
        sidebarCollapsed,
        spreadSidebar,
        toggleSidebar,
        collapseSidebar,
        presentationMode,
        setPresentationMode,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default React.memo(AppContextProvider) as typeof AppContextProvider;
